/**
 * Locale-aware navigation model.
 *
 * The mega menus, mobile accordion and footer columns are all DERIVED here from
 * three shared sources of truth:
 *   - the route map ({@link ../lib/i18n/routes.ts}) for every href,
 *   - the UI dictionary ({@link ../lib/i18n/dictionary.ts}) for every label,
 *   - the slim catalog index ({@link ../content/catalog-index.ts}) for content
 *     links (titles + slugs only - never the full lesson/article bodies).
 *
 * So switching language, renaming a route, or publishing a course updates the
 * whole nav/footer in one place - no per-component link lists to keep in sync.
 * Reading from the body-free index (instead of `getCatalog`) keeps every full
 * lesson/article/build body off the eager nav/footer bundle.
 */
import {
  BookOpen,
  Building2,
  GraduationCap,
  Library,
  Users,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { getCatalogIndex } from '../content/catalog-index'
import { getDictionary, localizedPath } from '../lib/i18n'
import type { Locale } from '../lib/i18n'
import { SITE_CREDIT } from '../lib/seo'

export type MegaMenuLink = {
  href: string
  label: string
  /** Pro course: the menu renders a small "Pro" badge next to the label. */
  pro?: boolean
}

export type MegaMenuDefinition = {
  id: string
  label: string
  icon: LucideIcon
  tagline: string
  overviewHref: string
  overviewLabel: string
  /**
   * Featured 4:3 plate image for this group's dropdown (served from
   * `public/`, so an absolute domain-root path). Pre-optimized webp; rendered
   * lazily and only after the menu is first opened so the landing never
   * eagerly fetches it. See {@link ../components/app-shell.tsx} MegaMenuBody.
   */
  image: string
  links: MegaMenuLink[]
}

export type MobileNavSection = {
  id: string
  label: string
  links: MegaMenuLink[]
  /** A plain top-level link (no accordion), e.g. Pricing. */
  href?: string
}

/** Top-level nav links next to the mega menus (no dropdown). */
export type TopNavLink = { id: string; href: string; label: string }

export type FooterModel = {
  tagline: string
  followUs: string
  legalLabel: string
  madeIn: string
  /** Honest "Built by <agency>" credit for the footer's legal region. */
  builtBy: { prefix: string; name: string; href: string }
  columns: Array<{ title: string; links: MegaMenuLink[] }>
  legalLinks: MegaMenuLink[]
}

/**
 * Plain top-level links rendered after the mega-menu triggers (desktop) and as
 * direct rows in the burger. Pricing lives here so every visitor is one click
 * from the plans, on every page, in every locale (localized slug).
 */
export function buildTopNavLinks(locale: Locale): TopNavLink[] {
  const dict = getDictionary(locale)
  return [
    { id: 'pricing', href: localizedPath('pricing', locale), label: dict.nav.links.pricing },
  ]
}

export function buildMegaMenus(locale: Locale): MegaMenuDefinition[] {
  const catalog = getCatalogIndex(locale)
  const dict = getDictionary(locale)
  const p = (key: Parameters<typeof localizedPath>[0], params?: Record<string, string>) =>
    localizedPath(key, locale, params)

  return [
    {
      id: 'courses',
      label: dict.nav.sections.courses.label,
      icon: GraduationCap,
      tagline: dict.nav.sections.courses.tagline,
      overviewHref: p('courses'),
      overviewLabel: dict.nav.sections.courses.overview,
      image: '/images/mega/courses.webp',
      links: catalog.courses.slice(0, 4).map((course) => ({
        href: p('course', { courseSlug: course.slug }),
        label: course.title,
        pro: course.tier === 'pro',
      })),
    },
    {
      id: 'community',
      label: dict.nav.sections.community.label,
      icon: Users,
      tagline: dict.nav.sections.community.tagline,
      overviewHref: p('community'),
      overviewLabel: dict.nav.sections.community.overview,
      image: '/images/mega/community.webp',
      links: [
        { href: p('communityTopics'), label: dict.nav.links.allTopics },
        {
          href: p('communityTopic', { topicSlug: 'workflow-reviews' }),
          label: dict.nav.links.workflowReviews,
        },
        {
          href: p('communityTopic', { topicSlug: 'tool-updates' }),
          label: dict.nav.links.toolUpdates,
        },
        {
          href: p('communityTopic', { topicSlug: 'showcase' }),
          label: dict.nav.links.showcases,
        },
      ],
    },
    {
      id: 'resources',
      label: dict.nav.sections.resources.label,
      icon: Library,
      tagline: dict.nav.sections.resources.tagline,
      overviewHref: p('resources'),
      overviewLabel: dict.nav.sections.resources.overview,
      image: '/images/mega/resources.webp',
      links: catalog.resources.slice(0, 4).map((resource) => ({
        href: p('resource', { resourceSlug: resource.slug }),
        label: resource.title,
      })),
    },
    {
      id: 'knowledge',
      label: dict.nav.sections.knowledge.label,
      icon: BookOpen,
      tagline: dict.nav.sections.knowledge.tagline,
      overviewHref: p('guides'),
      overviewLabel: dict.nav.sections.knowledge.overview,
      image: '/images/mega/knowledge.webp',
      links: [
        { href: p('guides'), label: dict.nav.links.guides },
        { href: p('glossary'), label: dict.nav.links.glossary },
        { href: p('compare'), label: dict.nav.links.compare },
        { href: p('tools'), label: dict.nav.links.tools },
        { href: p('fundamentals'), label: dict.nav.links.allFundamentals },
        { href: p('knowledge'), label: dict.footer.links.knowledge },
      ],
    },
    {
      id: 'company',
      label: dict.nav.sections.company.label,
      icon: Building2,
      tagline: dict.nav.sections.company.tagline,
      overviewHref: p('about'),
      overviewLabel: dict.nav.sections.company.overview,
      image: '/images/mega/company.webp',
      links: [
        { href: p('about'), label: dict.nav.links.about },
        { href: p('builds'), label: dict.nav.links.builds },
        { href: p('contact'), label: dict.nav.links.contact },
        { href: p('changelog'), label: dict.nav.links.changelog },
      ],
    },
  ]
}

export function buildMobileSections(locale: Locale): MobileNavSection[] {
  const dict = getDictionary(locale)
  const p = (key: Parameters<typeof localizedPath>[0], params?: Record<string, string>) =>
    localizedPath(key, locale, params)

  return [
    {
      id: 'courses',
      label: dict.nav.mobileSections.courses,
      links: [
        { href: p('courses'), label: dict.nav.links.allLessons },
        ...getCatalogIndex(locale)
          .courses.slice(0, 4)
          .map((course) => ({
            href: p('course', { courseSlug: course.slug }),
            label: course.title,
            pro: course.tier === 'pro',
          })),
      ],
    },
    ...buildTopNavLinks(locale).map((link) => ({
      id: link.id,
      label: link.label,
      href: link.href,
      links: [],
    })),
    {
      id: 'community',
      label: dict.nav.mobileSections.community,
      links: [
        { href: p('communityTopics'), label: dict.nav.links.topics },
        {
          href: p('communityTopic', { topicSlug: 'beginner-questions' }),
          label: dict.nav.links.questions,
        },
        {
          href: p('communityTopic', { topicSlug: 'showcase' }),
          label: dict.nav.links.showcases,
        },
        {
          href: p('communityTopic', { topicSlug: 'workflow-reviews' }),
          label: dict.nav.links.workflowReviews,
        },
        {
          href: p('communityTopic', { topicSlug: 'tool-updates' }),
          label: dict.nav.links.toolUpdates,
        },
      ],
    },
    {
      id: 'resources',
      label: dict.nav.mobileSections.resources,
      links: [
        { href: p('resources'), label: dict.nav.links.allResources },
        { href: p('guides'), label: dict.nav.links.guides },
        { href: p('glossary'), label: dict.nav.links.glossary },
        { href: p('compare'), label: dict.nav.links.compare },
        { href: p('tools'), label: dict.nav.links.tools },
        { href: p('knowledge'), label: dict.footer.links.knowledge },
        { href: p('fundamentals'), label: dict.nav.links.allFundamentals },
      ],
    },
    {
      id: 'company',
      label: dict.nav.mobileSections.company,
      links: [
        { href: p('about'), label: dict.nav.links.about },
        { href: p('builds'), label: dict.nav.links.builds },
        { href: p('contact'), label: dict.nav.links.contact },
        { href: p('newsletter'), label: dict.nav.links.newsletter },
        { href: p('changelog'), label: dict.nav.links.changelog },
        { href: p('settings'), label: dict.nav.links.account },
        { href: p('login'), label: dict.nav.links.login },
        { href: p('register'), label: dict.common.signUpCta },
      ],
    },
  ]
}

export function buildFooterModel(locale: Locale): FooterModel {
  const catalog = getCatalogIndex(locale)
  const dict = getDictionary(locale)
  const p = (key: Parameters<typeof localizedPath>[0], params?: Record<string, string>) =>
    localizedPath(key, locale, params)

  return {
    tagline: dict.footer.tagline,
    followUs: dict.footer.followUs,
    legalLabel: dict.footer.legalLabel,
    madeIn: dict.footer.madeIn,
    builtBy: {
      prefix: dict.footer.builtBy,
      name: SITE_CREDIT.name,
      href: SITE_CREDIT.href,
    },
    columns: [
      {
        title: dict.footer.columns.courses,
        links: [
          { href: p('courses'), label: dict.common.allCourses },
          { href: p('pricing'), label: dict.nav.links.pricing },
          ...catalog.courses.map((course) => ({
            href: p('course', { courseSlug: course.slug }),
            label: course.title,
          })),
        ],
      },
      {
        title: dict.footer.columns.community,
        links: [
          { href: p('community'), label: dict.footer.links.communityHub },
          { href: p('communityTopics'), label: dict.footer.links.allTopics },
          {
            href: p('communityTopic', { topicSlug: 'workflow-reviews' }),
            label: dict.footer.links.workflowReviews,
          },
          {
            href: p('communityTopic', { topicSlug: 'tool-updates' }),
            label: dict.footer.links.toolUpdates,
          },
          {
            href: p('communityTopic', { topicSlug: 'showcase' }),
            label: dict.footer.links.showcases,
          },
          {
            href: p('communityTopic', { topicSlug: 'beginner-questions' }),
            label: dict.footer.links.beginnerQuestions,
          },
        ],
      },
      {
        title: dict.footer.columns.resources,
        links: [
          { href: p('guides'), label: dict.footer.links.guides },
          { href: p('glossary'), label: dict.footer.links.glossary },
          { href: p('compare'), label: dict.footer.links.compare },
          { href: p('tools'), label: dict.footer.links.tools },
          { href: p('knowledge'), label: dict.footer.links.knowledge },
          { href: p('fundamentals'), label: dict.footer.links.fundamentals },
          { href: p('resources'), label: dict.footer.links.allResources },
          ...catalog.resources.slice(0, 2).map((resource) => ({
            href: p('resource', { resourceSlug: resource.slug }),
            label: resource.title,
          })),
        ],
      },
      {
        title: dict.footer.columns.company,
        links: [
          { href: p('about'), label: dict.footer.links.about },
          { href: p('builds'), label: dict.footer.links.builds },
          { href: p('contact'), label: dict.footer.links.contact },
          { href: p('newsletter'), label: dict.footer.links.newsletter },
          { href: p('changelog'), label: dict.footer.links.changelog },
          { href: p('settings'), label: dict.footer.links.account },
          { href: p('login'), label: dict.footer.links.login },
          { href: p('register'), label: dict.common.signUpCta },
        ],
      },
    ],
    legalLinks: [
      { href: p('imprint'), label: dict.nav.links.imprint },
      { href: p('privacy'), label: dict.nav.links.privacy },
      { href: p('cookies'), label: dict.nav.links.cookieSettings },
      { href: p('terms'), label: dict.nav.links.terms },
      { href: p('accessibility'), label: dict.nav.links.accessibility },
      { href: p('apiDocs'), label: dict.footer.links.apiDocs },
    ],
  }
}
